import React, { useEffect, useState } from "react";
import { Button, Col, Row, Tag, Table, Modal, Input, Image } from "antd";
import Text from "antd/lib/typography/Text";
import TextArea from "antd/lib/input/TextArea";

import "./index.css";
import Sidebar from "../../../components/Sidebar";
import { DateFormat, getApi, saveApi, errorMessage } from "../../../helpers";
import { getUserData } from "../../../helpers/permissions";

const STATUS_COLOR = {
  draft: "default",
  awaiting_proof: "orange",
  processing: "blue",
  auto_resolved: "green",
  refund_pending: "gold",
  escalated_bot: "purple",
  escalated_agent: "volcano",
  closed: "green",
  rejected: "red",
};

const itemColumns = [
  { title: "Product Name", dataIndex: "product_name", key: "product_name" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", width: 55 },
  {
    title: "Price (₹)",
    dataIndex: "line_total",
    key: "line_total",
    width: 90,
    render: (t) => <div style={{ textAlign: "right" }}>₹ {t}</div>,
  },
];

export default function IssueSidebar({ visible, onClose, issue_id, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [actionModal, setActionModal] = useState(false);
  const [action, setAction] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (issue_id) fetchIssue();
  }, []);

  const fetchIssue = async () => {
    setLoading(true);
    const res = await getApi(
      { issue_id, type: "admin" },
      "getIssueById",
      "ecomm/issue/"
    );
    if (res.data) setData(res.data);
    setLoading(false);
  };

  const resetModal = () => {
    setActionModal(false);
    setAction("");
    setAmount("");
    setNote("");
  };

  const handleResolve = async () => {
    if (!action) return errorMessage("Please select an action");
    if ((action === "coupon" || action === "refund") && !amount)
      return errorMessage("Please enter amount");

    const userData = getUserData();
    setSubmitting(true);
    const payload = {
      issue_id: data.id,
      agent_id: userData?.id || userData?.user_id,
      action,
      note,
    };
    if (action !== "reject") payload.amount = parseFloat(amount);

    const res = await saveApi(
      payload,
      "Ticket Resolved",
      "dashboardIssueAction",
      "ecomm/issue/"
    );
    setSubmitting(false);
    if (res.success) {
      resetModal();
      fetchIssue();
      onUpdate?.();
    }
  };

  const canResolve = data.status === "escalated_agent";

  return (
    <>
      {actionModal && (
        <Modal
          visible
          centered
          maskClosable={false}
          title="Resolve Ticket"
          okText="Confirm"
          cancelText="Cancel"
          confirmLoading={submitting}
          onCancel={resetModal}
          onOk={handleResolve}
        >
          <div style={{ marginBottom: 16 }}>
            <Text strong>Select Action</Text>
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <Button
                type={action === "coupon" ? "primary" : "default"}
                onClick={() => setAction("coupon")}
              >
                Issue Coupon
              </Button>
              <Button
                type={action === "refund" ? "primary" : "default"}
                onClick={() => setAction("refund")}
              >
                Initiate Refund
              </Button>
              <Button
                danger
                type={action === "reject" ? "primary" : "default"}
                onClick={() => setAction("reject")}
              >
                Reject
              </Button>
            </div>
          </div>
          {(action === "coupon" || action === "refund") && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Amount (₹)</Text>
              <Input
                style={{ marginTop: 8 }}
                type="number"
                min={0}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
          )}
          <div>
            <Text strong>Note (optional)</Text>
            <TextArea
              style={{ marginTop: 8 }}
              rows={2}
              maxLength={256}
              showCount
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Agent note..."
            />
          </div>
        </Modal>
      )}

      <Sidebar
        loading={loading}
        visible={visible}
        onClose={onClose}
        title="Issue Details"
        footer={
          canResolve ? (
            <div style={{ textAlign: "right" }}>
              <Button type="primary" onClick={() => setActionModal(true)}>
                Resolve Ticket
              </Button>
            </div>
          ) : null
        }
      >
        <Row>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Issue Id</Text>
            <br />
            <Text>{data.id}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Order Id</Text>
            <br />
            <Text>{data.order_id}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Customer Name</Text>
            <br />
            <Text>{data.first_name} {data.last_name}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Mobile</Text>
            <br />
            <Text>{data.mobile}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Email</Text>
            <br />
            <Text>{data.email || "-"}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Issue Type</Text>
            <br />
            <Text>{data.issue_type_name || "-"}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Status</Text>
            <br />
            <Tag
              color={STATUS_COLOR[data.status] || "default"}
              style={{ textTransform: "capitalize", marginTop: 4 }}
            >
              {data.status?.replace(/_/g, " ") || "N/A"}
            </Tag>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Total Value</Text>
            <br />
            <Text>₹ {data.total_value}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">Created At</Text>
            <br />
            <Text>{DateFormat(data.created_at)}</Text>
          </Col>
          {data.resolved_at && (
            <Col className="lblc" xs={24} md={12}>
              <Text style={{ fontSize: 11 }} type="secondary">Resolved At</Text>
              <br />
              <Text>{DateFormat(data.resolved_at)}</Text>
            </Col>
          )}
          {data.description && (
            <Col className="lblc" xs={24}>
              <Text style={{ fontSize: 11 }} type="secondary">Description</Text>
              <br />
              <Text>{data.description}</Text>
            </Col>
          )}
          {data.ai_summary && (
            <Col className="lblc" xs={24}>
              <Text style={{ fontSize: 11 }} type="secondary">AI Summary</Text>
              <br />
              <Text>{data.ai_summary}</Text>
            </Col>
          )}
        </Row>

        {data.resolution && (
          <div
            style={{
              marginTop: 16,
              padding: "12px 16px",
              background: "#f6f8fa",
              borderRadius: 6,
              border: "1px solid #e8e8e8",
            }}
          >
            <Text strong>Resolution</Text>
            <Row style={{ marginTop: 8 }}>
              <Col xs={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Type</Text>
                <br />
                <Text style={{ textTransform: "capitalize" }}>
                  {data.resolution.type}
                </Text>
              </Col>
              <Col xs={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Amount</Text>
                <br />
                <Text>₹ {data.resolution.amount}</Text>
              </Col>
              {data.resolution.coupon_code && (
                <Col xs={24} style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Coupon Code</Text>
                  <br />
                  <Text copyable>{data.resolution.coupon_code}</Text>
                </Col>
              )}
            </Row>
          </div>
        )}

        {data.items && data.items.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <Text strong>Issue Items</Text>
            <Table
              size="small"
              style={{ marginTop: 8 }}
              columns={itemColumns}
              dataSource={data.items}
              rowKey="id"
              pagination={false}
            />
          </div>
        )}

        {data.proofs && data.proofs.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <Text strong>Proof Images</Text>
            <div
              style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}
            >
              <Image.PreviewGroup>
                {data.proofs.map((p) => (
                  <Image
                    key={p.id}
                    width={80}
                    height={80}
                    src={p.image_url}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                  />
                ))}
              </Image.PreviewGroup>
            </div>
          </div>
        )}

        {data.log && data.log.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <Text strong>Activity Log</Text>
            <div className="cnt-nav" style={{ marginTop: 12 }}>
              <div className="lst-itms">
                <ul className="lst">
                  {data.log.map((l, i) => (
                    <li
                      key={l.id}
                      className={
                        i < data.log.length - 1 ? "itm compltd" : "itm activ"
                      }
                    >
                      <Text strong style={{ textTransform: "capitalize" }}>
                        {l.to_status?.replace(/_/g, " ")}
                      </Text>
                      <br />
                      <Text style={{ fontSize: 12, color: "#666" }}>
                        {DateFormat(l.created_at)}
                      </Text>
                      {l.actor && (
                        <>
                          <br />
                          <Text style={{ fontSize: 11, color: "#999" }}>
                            by {l.actor}
                          </Text>
                        </>
                      )}
                      {l.note && (
                        <>
                          <br />
                          <Text style={{ fontSize: 11, color: "#999" }}>
                            Note: {l.note}
                          </Text>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Sidebar>
    </>
  );
}
